/**
 * La prochaine action — chunk 9.BF.1.
 *
 * ## Le défaut réparé
 *
 * Le conseiller devait parcourir onze sections pour savoir quoi faire. La
 * réponse existait pourtant déjà : `GET /v1/projects/:id/completeness` renvoie
 * `next_action`, `waiting_on`, `overdue_count` et `due_soon_count` depuis
 * 9.AC.1. Elle était enterrée en quatrième position, au milieu de trois autres
 * sections qui prétendaient répondre à la même question.
 *
 * ## Une seule autorité
 *
 * Ce bloc **ne calcule rien**. Il lit le verdict serveur et le met en premier.
 * Recalculer une prochaine action côté web produirait un deuxième avis — et le
 * jour où les deux divergent, personne ne saurait lequel croire.
 *
 * La complétude ne certifie que les requis déclarés. Les autres conditions
 * d’envoi restent à consulter dans la préparation de la demande.
 */

import { Workflow } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useProjectCompleteness } from "../../../lib/queries/projectCompleteness";
import { useProject } from "../../../lib/queries/projects";

import { EtatOperationnelAction } from "./EtatOperationnel";

/** Le ton suit l'urgence, pas le statut : on regarde ce qui presse. */
function ton(overdue: number, dueSoon: number, aFaire: boolean): string {
  if (overdue > 0) return "border-red-300 bg-red-50";
  if (dueSoon > 0) return "border-amber-300 bg-amber-50";
  return aFaire ? "border-brand-200 bg-brand-50" : "border-slate-200 bg-slate-50";
}

/** Le geste qui correspond à l'état engagé. Un lien générique n'apprend rien. */
const RACCOURCI: Record<string, { cle: string; suffixe: string }> = {
  awaiting_insurers: { cle: "track", suffixe: "?view=insurers" },
  partial_responses: { cle: "track", suffixe: "?view=insurers" },
  ready_to_compare: { cle: "compare", suffixe: "/comparison" },
};

export function ProchaineAction({ projectId }: { readonly projectId: string }) {
  const { t } = useTranslation();
  const verdict = useProjectCompleteness(projectId);
  // 9.BH.1 — le détail porte l'état opérationnel, comme la liste : c'est la même
  // fonction 0082, jointe une fois de plus. Le déduire ici du suivi assureur
  // écrirait la règle une seconde fois.
  const dossier = useProject(projectId);

  if (dossier.data?.activity_scope === "history") {
    return (
      <section
        aria-labelledby="prochaine-action"
        className="rounded-xl border border-slate-200 bg-slate-50 p-5"
      >
        <h2
          id="prochaine-action"
          className="text-xs font-medium uppercase tracking-wide text-slate-600"
        >
          {t("project_hub.next_action.label")}
        </h2>
        <p className="my-2 font-semibold">{t("projects.operational.history")}</p>
        <EtatOperationnelAction
          projectId={projectId}
          etat={dossier.data.operational_state}
          activityScope="history"
        />
      </section>
    );
  }

  if (verdict.isPending) {
    return (
      <div aria-busy="true" aria-live="polite">
        <span className="sr-only">{t("completeness.loading")}</span>
        <div className="h-20 animate-pulse rounded-xl bg-slate-100 motion-reduce:animate-none" />
      </div>
    );
  }

  // Une erreur ici ne doit pas masquer le reste du dossier : le bloc s'efface,
  // et les sections qui suivent restent lisibles.
  if (verdict.isError || !verdict.data) return null;

  const v = verdict.data;
  const enRetard = v.overdue_count ?? 0;
  const bientot = v.due_soon_count ?? 0;

  /**
   * 9.BH.1 — deux sources qui ont raison, et une seule qui parle en premier.
   *
   * Sur un dossier dont les assureurs ont répondu, la complétude annonçait
   * « définir ce que ce dossier doit réunir » : vrai dans son domaine, absurde
   * à l'écran. La précédence vit dans `@kollectivia/shared` — une définition,
   * un test, et aucune des deux sources modifiée.
   */
  const principale = Workflow.actionPrincipale({
    etatOperationnel: dossier.data?.operational_state ?? null,
    actionCompletude: v.next_action,
  });
  const engage = principale.voix === "operationnelle" && principale.etat !== null;
  const raccourci = engage ? RACCOURCI[principale.etat!] : undefined;
  const rienAFaire =
    !engage && v.completeness_status === "ready" && v.next_action !== "define_requirements";

  return (
    <section
      aria-labelledby="prochaine-action"
      className={`rounded-xl border p-5 ${ton(enRetard, bientot, !rienAFaire)}`}
    >
      <h2
        id="prochaine-action"
        className="text-xs font-medium uppercase tracking-wide text-slate-600"
      >
        {t("project_hub.next_action.label")}
      </h2>

      <p className="mt-1 text-lg font-semibold text-slate-900">
        {engage
          ? t(`project_hub.next_action.operational.${principale.etat}`)
          : rienAFaire
            ? t("project_hub.next_action.nothing")
            : t(`completeness.next_action.${v.next_action}`)}
      </p>

      {rienAFaire && (
        <Link
          to={`/projects/${projectId}#quote-requests`}
          className="focus-visible:ring-brand-500 text-brand-700 mt-2 inline-flex min-h-11 items-center rounded-md text-sm font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("project_hub.next_action.review_requests")}
        </Link>
      )}

      {raccourci && (
        <Link
          to={`/projects/${projectId}${raccourci.suffixe}`}
          className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 mt-3 inline-flex touch-manipulation rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t(`projects.operational.cta.${raccourci.cle}`)}
        </Link>
      )}

      {/* La lacune de complétude n'est pas effacée : elle cesse seulement d'être
          ce qu'on doit faire à l'instant. L'effacer ferait disparaître du
          dossier une information que personne ne redemanderait. */}
      {principale.avertissement && (
        <p className="mt-2 text-sm text-slate-600">
          {t(`project_hub.next_action.warning.${principale.avertissement}`)}
        </p>
      )}

      {(enRetard > 0 || bientot > 0) && (
        <p className="mt-1 text-sm text-slate-700">
          {enRetard > 0 && t("completeness.overdue", { count: enRetard })}
          {enRetard > 0 && bientot > 0 && " · "}
          {bientot > 0 && t("completeness.due_soon", { count: bientot })}
        </p>
      )}

      {v.requirements_missing > 0 && (
        <p className="mt-1 text-sm text-slate-600">
          {t("completeness.summary.counts", {
            satisfied: v.requirements_satisfied,
            total: v.requirements_total,
          })}
        </p>
      )}
    </section>
  );
}
